import type { PublicAccount } from './api';

/**
 * The keymap is data, not behaviour: the dispatcher (hooks/useKeyboard.ts), the
 * command palette and the '?' cheat sheet all render from this one array, so a
 * binding can never drift between what fires and what is advertised.
 *
 * Scope means *who owns the key*:
 *   global   — the shell dispatcher owns it (registered by CommandPalette)
 *   list     — the thread list pane owns it, on its own listener
 *   composer — the open composer owns it
 * There is deliberately no Drafts target, no spam/mute/label/move: the HTTP
 * contract has no such action, and a key that pretends to work is worse than
 * one that is absent.
 * A command with no `run` is documentation only: the key works, but the pane
 * that owns it implements it, so the palette cannot invoke it as an action.
 */

export type CommandScope = 'global' | 'list' | 'reading' | 'composer';

export type CommandGroup =
  | 'Navigation'
  | 'Triage'
  | 'Compose'
  | 'Search'
  | 'Go to'
  | 'View'
  | 'Accounts'
  | 'Application';

/** Where `g`-chords can land. The host maps these onto view + category state. */
export type GoTarget =
  | 'inbox'
  | 'all'
  | 'starred'
  | 'sent'
  | 'done'
  | 'snoozed'
  | 'unread'
  | 'newsletters'
  | 'receipts';

/**
 * Everything a command is allowed to touch. Commands never import the store —
 * the host owns state and hands the dispatcher an implementation of this, which
 * keeps the keymap testable and free of circular imports.
 */
export interface CommandContext {
  /* --- environment --- */
  accounts: PublicAccount[];
  accountFilter: number | null;
  aiEnabled: boolean;
  selection: { threadKey: string | null; count: number };
  hasPendingSend: boolean;

  /* --- view --- */
  goBack(): void;
  goTo(target: GoTarget): void;
  setAccountFilter(id: number | null): void;
  toggleFocusMode(): void;

  /* --- triage, acting on the selected thread --- */
  archive(restore?: boolean): void;
  trash(): void;
  snooze(): void;
  toggleStar(): void;
  toggleRead(): void;
  undo(): void;

  /* --- compose --- */
  compose(popout?: boolean): void;
  reply(all?: boolean, popout?: boolean): void;
  forward(popout?: boolean): void;
  flushPendingSend(): void;
  /* Supplied by the open composer, which registers over the global context. */
  send?(): void;
  sendAndArchive?(): void;
  sendLater?(): void;
  discardDraft?(): void;
  openAiInstruction?(): void;

  /* --- overlays --- */
  openPalette(): void;
  openCheatSheet(): void;
  focusSearch(allAccounts?: boolean): void;
}

export type Command = {
  id: string;
  label: string;
  /** Canonical chord strings, e.g. 'Mod+k', 'Shift+e', 'g i'. First one is displayed. */
  keys: string[];
  scope: CommandScope;
  group: CommandGroup;
  /** Extra words the palette's fuzzy matcher should consider. */
  keywords?: string;
  /** Hidden from palette and cheat sheet listings; still dispatchable. */
  paletteHidden?: boolean;
  /** Fires even while a text field has focus. Only ever true for modified keys. */
  allowInEditable?: boolean;
  /** Default true; set false to let the browser keep the key after we act. */
  preventDefault?: boolean;
  when?: (ctx: CommandContext) => boolean;
  /** Omit when another pane implements the key: the entry becomes documentation. */
  run?: (ctx: CommandContext) => void;
};

export const isMac =
  typeof navigator !== 'undefined' && /mac|iphone|ipad/i.test(navigator.platform || navigator.userAgent);

/**
 * Ctrl+1..9 is browser tab switching, so the Ctrl aliases only exist in an
 * installed window, where there is no tab strip to steal from.
 */
export const isStandalone =
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(display-mode: standalone)').matches;

const MOD = 'Mod';

function accountKeys(n: number): string[] {
  if (!isStandalone) return [`Alt+${n}`];
  // Off macOS the platform modifier already *is* Ctrl, so the alias is Mod.
  return [`Alt+${n}`, isMac ? `Ctrl+${n}` : `Mod+${n}`];
}

function enabledAccounts(ctx: CommandContext): PublicAccount[] {
  return ctx.accounts.filter((a) => a.enabled);
}

function hasThread(ctx: CommandContext): boolean {
  return ctx.selection.threadKey !== null || ctx.selection.count > 0;
}

const goTargets: { key: string; target: GoTarget; label: string }[] = [
  { key: 'i', target: 'inbox', label: 'Go to Inbox' },
  { key: 'a', target: 'all', label: 'Go to All mail' },
  { key: 's', target: 'starred', label: 'Go to Starred' },
  { key: 't', target: 'sent', label: 'Go to Sent' },
  { key: 'e', target: 'done', label: 'Go to Done' },
  { key: 'h', target: 'snoozed', label: 'Go to Snoozed' },
  { key: 'u', target: 'unread', label: 'Go to Unread' },
  { key: 'n', target: 'newsletters', label: 'Go to Newsletters' },
  { key: 'r', target: 'receipts', label: 'Go to Receipts' },
];

const goCommands: Command[] = goTargets.map(({ key, target, label }) => ({
  id: `go.${target}`,
  label,
  keys: [`g ${key}`],
  scope: 'global',
  group: 'Go to',
  keywords: target,
  run: (ctx) => ctx.goTo(target),
}));

const accountCommands: Command[] = Array.from({ length: 8 }, (_, i) => {
  const n = i + 1;
  return {
    id: `account.switch.${n}`,
    label: `Switch to account ${n}`,
    keys: accountKeys(n),
    scope: 'global',
    group: 'Accounts',
    // The palette lists the real accounts by name and hue instead of these slots.
    paletteHidden: true,
    when: (ctx) => enabledAccounts(ctx).length >= n,
    run: (ctx) => {
      const account = enabledAccounts(ctx)[i];
      if (account) ctx.setAccountFilter(account.id);
    },
  } satisfies Command;
});

export const commands: Command[] = [
  /* ------------------------------- Application ------------------------------ */
  {
    id: 'palette.open',
    label: 'Command palette',
    keys: [`${MOD}+k`],
    scope: 'global',
    group: 'Application',
    keywords: 'commands run actions',
    allowInEditable: true,
    run: (ctx) => ctx.openPalette(),
  },
  {
    id: 'help.shortcuts',
    label: 'Keyboard shortcuts',
    keys: ['?'],
    scope: 'global',
    group: 'Application',
    keywords: 'help cheat sheet keys',
    run: (ctx) => ctx.openCheatSheet(),
  },
  {
    id: 'nav.back',
    label: 'Back / close',
    keys: ['Escape'],
    scope: 'global',
    group: 'Application',
    keywords: 'escape dismiss',
    allowInEditable: true,
    // Other surfaces listen for Escape too; taking the default away would
    // strand a native picker or an IME candidate window.
    preventDefault: false,
    run: (ctx) => ctx.goBack(),
  },
  {
    id: 'edit.undo',
    label: 'Undo last action',
    keys: ['z'],
    scope: 'global',
    group: 'Application',
    keywords: 'revert restore unsend',
    run: (ctx) => ctx.undo(),
  },
  {
    id: 'view.focus',
    label: 'Toggle focus mode',
    keys: [`${MOD}+Shift+\\`],
    scope: 'global',
    group: 'View',
    keywords: 'zen distraction free hide sidebar',
    allowInEditable: true,
    run: (ctx) => ctx.toggleFocusMode(),
  },

  /* -------------------------------- Accounts ------------------------------- */
  {
    id: 'account.all',
    label: 'All inboxes',
    keys: accountKeys(0),
    scope: 'global',
    group: 'Accounts',
    keywords: 'unified every account',
    run: (ctx) => ctx.setAccountFilter(null),
  },
  ...accountCommands,

  /* --------------------------------- Search -------------------------------- */
  {
    id: 'search.focus',
    label: 'Search',
    keys: ['/'],
    scope: 'global',
    group: 'Search',
    keywords: 'find filter query',
    run: (ctx) => ctx.focusSearch(false),
  },
  {
    id: 'search.all',
    label: 'Search all accounts',
    keys: [`${MOD}+/`],
    scope: 'global',
    group: 'Search',
    keywords: 'find everywhere unified',
    allowInEditable: true,
    run: (ctx) => ctx.focusSearch(true),
  },

  /* --------------------------------- Go to --------------------------------- */
  ...goCommands,

  /* ------------------------- Navigation (list-owned) ------------------------ */
  {
    id: 'nav.next',
    label: 'Next conversation',
    keys: ['j'],
    scope: 'list',
    group: 'Navigation',
    keywords: 'down',
  },
  {
    id: 'nav.prev',
    label: 'Previous conversation',
    keys: ['k'],
    scope: 'list',
    group: 'Navigation',
    keywords: 'up',
  },
  {
    id: 'nav.open',
    label: 'Open conversation',
    keys: ['Enter', 'o'],
    scope: 'list',
    group: 'Navigation',
  },
  {
    id: 'triage.select',
    label: 'Select conversation',
    keys: ['x'],
    scope: 'list',
    group: 'Navigation',
    keywords: 'check tick',
  },
  {
    id: 'triage.select.down',
    label: 'Extend selection down',
    keys: ['Shift+j'],
    scope: 'list',
    group: 'Navigation',
  },
  {
    id: 'triage.select.up',
    label: 'Extend selection up',
    keys: ['Shift+k'],
    scope: 'list',
    group: 'Navigation',
  },

  /* --------------------------------- Triage -------------------------------- */
  {
    id: 'triage.archive',
    label: 'Archive',
    keys: ['e'],
    scope: 'list',
    group: 'Triage',
    keywords: 'done remove inbox',
    when: hasThread,
    run: (ctx) => ctx.archive(false),
  },
  {
    id: 'triage.unarchive',
    label: 'Move back to Inbox',
    keys: ['Shift+e'],
    scope: 'global',
    group: 'Triage',
    keywords: 'unarchive restore',
    when: hasThread,
    run: (ctx) => ctx.archive(true),
  },
  {
    id: 'triage.snooze',
    label: 'Snooze until tomorrow',
    keys: ['h', 'b'],
    scope: 'global',
    group: 'Triage',
    keywords: 'later remind hibernate',
    when: hasThread,
    run: (ctx) => ctx.snooze(),
  },
  {
    id: 'triage.star',
    label: 'Star',
    keys: ['s'],
    scope: 'list',
    group: 'Triage',
    keywords: 'flag favourite',
    when: hasThread,
    run: (ctx) => ctx.toggleStar(),
  },
  {
    id: 'triage.trash',
    label: 'Delete',
    keys: ['#'],
    scope: 'list',
    group: 'Triage',
    keywords: 'trash bin remove',
    when: hasThread,
    run: (ctx) => ctx.trash(),
  },
  {
    id: 'triage.read',
    label: 'Toggle read / unread',
    keys: ['u'],
    scope: 'list',
    group: 'Triage',
    keywords: 'seen unseen mark',
    when: hasThread,
    run: (ctx) => ctx.toggleRead(),
  },

  /* -------------------------------- Compose -------------------------------- */
  {
    id: 'compose.new',
    label: 'Compose',
    keys: ['c'],
    scope: 'global',
    group: 'Compose',
    keywords: 'new write mail',
    run: (ctx) => ctx.compose(false),
  },
  {
    id: 'compose.new.popout',
    label: 'Compose in a window',
    keys: ['Shift+c'],
    scope: 'global',
    group: 'Compose',
    keywords: 'popout float new window',
    run: (ctx) => ctx.compose(true),
  },
  {
    id: 'compose.reply',
    label: 'Reply',
    keys: ['r'],
    scope: 'global',
    group: 'Compose',
    when: hasThread,
    run: (ctx) => ctx.reply(false, false),
  },
  {
    id: 'compose.reply.popout',
    label: 'Reply in a window',
    keys: ['Shift+r'],
    scope: 'global',
    group: 'Compose',
    keywords: 'popout float',
    when: hasThread,
    run: (ctx) => ctx.reply(false, true),
  },
  {
    id: 'compose.replyAll',
    label: 'Reply all',
    keys: ['a'],
    scope: 'global',
    group: 'Compose',
    when: hasThread,
    run: (ctx) => ctx.reply(true, false),
  },
  {
    id: 'compose.forward',
    label: 'Forward',
    keys: ['f'],
    scope: 'global',
    group: 'Compose',
    when: hasThread,
    run: (ctx) => ctx.forward(false),
  },
  {
    id: 'compose.forward.popout',
    label: 'Forward in a window',
    keys: ['Shift+f'],
    scope: 'global',
    group: 'Compose',
    keywords: 'popout float',
    when: hasThread,
    run: (ctx) => ctx.forward(true),
  },
  {
    id: 'compose.send',
    label: 'Send',
    keys: [`${MOD}+Enter`],
    scope: 'composer',
    group: 'Compose',
    allowInEditable: true,
    run: (ctx) => ctx.send?.(),
  },
  {
    id: 'compose.sendArchive',
    label: 'Send and archive',
    keys: [`${MOD}+Shift+Enter`],
    scope: 'composer',
    group: 'Compose',
    allowInEditable: true,
    run: (ctx) => ctx.sendAndArchive?.(),
  },
  {
    id: 'compose.sendLater',
    label: 'Send later…',
    keys: [`${MOD}+Shift+l`],
    scope: 'composer',
    group: 'Compose',
    keywords: 'schedule delay',
    allowInEditable: true,
    run: (ctx) => ctx.sendLater?.(),
  },
  {
    id: 'send.flush',
    label: 'Send now (skip the undo window)',
    keys: [`${MOD}+Shift+z`],
    scope: 'global',
    group: 'Compose',
    allowInEditable: true,
    when: (ctx) => ctx.hasPendingSend,
    run: (ctx) => ctx.flushPendingSend(),
  },
  {
    id: 'compose.discard',
    label: 'Discard draft',
    keys: [`${MOD}+Shift+,`],
    scope: 'composer',
    group: 'Compose',
    keywords: 'delete throw away',
    allowInEditable: true,
    run: (ctx) => ctx.discardDraft?.(),
  },
  {
    id: 'compose.ai',
    label: 'Draft with AI…',
    keys: [`${MOD}+Shift+i`],
    scope: 'composer',
    group: 'Compose',
    keywords: 'assistant instruction write',
    allowInEditable: true,
    when: (ctx) => ctx.aiEnabled,
    run: (ctx) => ctx.openAiInstruction?.(),
  },
];

export const commandsById: ReadonlyMap<string, Command> = new Map(commands.map((c) => [c.id, c]));

export function commandsInGroup(group: CommandGroup): Command[] {
  return commands.filter((c) => c.group === group && !c.paletteHidden);
}

export const groupOrder: CommandGroup[] = [
  'Navigation',
  'Triage',
  'Compose',
  'Go to',
  'Search',
  'View',
  'Accounts',
  'Application',
];

/* --------------------------------- display -------------------------------- */

const TOKEN_LABELS: Record<string, string> = {
  Mod: isMac ? '⌘' : 'Ctrl',
  Ctrl: isMac ? '⌃' : 'Ctrl',
  Alt: isMac ? '⌥' : 'Alt',
  Shift: '⇧',
  Enter: '⏎',
  Escape: 'Esc',
  Space: 'Space',
  Tab: '⇥',
  Delete: 'Del',
  Backspace: '⌫',
  ArrowUp: '↑',
  ArrowDown: '↓',
  ArrowLeft: '←',
  ArrowRight: '→',
};

/** 'Mod+Shift+e' → [['⌘','⇧','E']]; 'g i' → [['G'],['I']] (segments = chord steps). */
export function formatChord(chord: string): string[][] {
  return chord.split(' ').map((step) =>
    step.split('+').map((token) => {
      const mapped = TOKEN_LABELS[token];
      if (mapped) return mapped;
      return token.length === 1 ? token.toUpperCase() : token;
    }),
  );
}

/** Flat single-line rendering, for places where a chip grid would be overkill. */
export function formatChordText(chord: string): string {
  return formatChord(chord)
    .map((step) => step.join(isMac ? '' : '+'))
    .join(' then ');
}
